import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { safeBlobGet } from '../utils/fetchPdfBlob';
import { downloadBlob } from '../utils/downloadBlob';
import { AppShell } from '../components/AppShell';
import { SendDocumentEmailDialog } from '../components/documents/SendDocumentEmailDialog';
import { QuickAddFAB } from '../components/QuickAddFAB';
import { GlobalProgressHero } from '../components/planning/GlobalProgressHero';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { DeleteConfirmDialog } from '../components/DeleteConfirmDialog';
import {
  Plus, CalendarBlank, ListChecks,
  ArrowLeft, ChartBar, Flag, FilePdf, Upload,
} from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';
import { format, parseISO, differenceInDays, addDays } from 'date-fns';
import { toast } from 'sonner';

import { GanttChart } from '../components/planning/GanttChart';
import { CalendarView } from '../components/planning/CalendarView';
import { TaskListView } from '../components/planning/TaskListView';
import { TaskFormSheet } from '../components/planning/TaskFormSheet';
import { ImportPlanningDialog } from '../components/planning/ImportPlanningDialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/* ========== MAIN COMPONENT ========== */
export function PlanningPage() {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [chantiers, setChantiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('gantt');
  const [filterChantier, setFilterChantier] = useState('all');
  const [emailOpen, setEmailOpen] = useState(false);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [formData, setFormData] = useState({
    chantier_id: '', title: '', description: '',
    start_date: '', end_date: '', lot: '',
    responsible: '', entreprise_id: '', status: 'pending', priority: 'medium', progress: 0,
    task_type: 'task'
  });
  const [submitting, setSubmitting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [entreprises, setEntreprises] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const [tasksRes, chantiersRes] = await Promise.all([
        axios.get(`${API}/tasks`, { withCredentials: true }),
        axios.get(`${API}/chantiers`, { withCredentials: true })
      ]);
      setTasks(tasksRes.data || []);
      setChantiers((chantiersRes.data || []).filter(c => c && c.id && c.name));
    } catch (_err) { setTasks([]); setChantiers([]); }
    finally { setLoading(false); }
  }, [setTasks, setChantiers, setLoading]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreateSheet = (date = null, isMilestone = false) => {
    setEditingTask(null);
    const today = format(new Date(), 'yyyy-MM-dd');
    // Si un chantier est déjà sélectionné (filtre actif), on pré-remplit
    // pour éviter à l'utilisateur de le re-sélectionner à chaque tâche.
    const prefilledChantier = filterChantier !== 'all' ? filterChantier : '';
    setFormData({
      chantier_id: prefilledChantier, title: '', description: '',
      start_date: date ? format(date, 'yyyy-MM-dd') : today,
      end_date: date ? format(date, 'yyyy-MM-dd') : today,
      lot: '', responsible: '', entreprise_id: '', status: 'pending', priority: 'medium', progress: 0,
      task_type: isMilestone ? 'milestone' : 'task'
    });
    if (prefilledChantier) fetchEntreprises(prefilledChantier);
    else setEntreprises([]);
    setIsSheetOpen(true);
  };

  const openEditSheet = (task) => {
    setEditingTask(task);
    setFormData({
      chantier_id: task.chantier_id || '',
      title: task.title || '', description: task.description || '',
      start_date: task.start_date || '', end_date: task.end_date || '',
      lot: task.lot || '', responsible: task.responsible || '',
      entreprise_id: task.entreprise_id || '',
      status: task.status || 'pending', priority: task.priority || 'medium',
      progress: task.progress || 0,
      task_type: task.task_type || 'task'
    });
    if (task.chantier_id) fetchEntreprises(task.chantier_id);
    setIsSheetOpen(true);
  };

  const fetchEntreprises = async (chantierId) => {
    if (!chantierId) { setEntreprises([]); return; }
    try {
      const res = await axios.get(`${API}/chantiers/${chantierId}/entreprises`, { withCredentials: true });
      setEntreprises(res.data || []);
    } catch (_err) { setEntreprises([]); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...formData };
      if (payload.task_type === 'milestone') {
        payload.end_date = payload.start_date;
      }
      if (editingTask) {
        await axios.put(`${API}/tasks/${editingTask.id}`, payload, { withCredentials: true });
        toast.success('Tâche mise a jour');
      } else {
        await axios.post(`${API}/tasks`, payload, { withCredentials: true });
        toast.success(payload.task_type === 'milestone' ? 'Jalon cree avec succes' : 'Tâche créée avec succes');
      }
      setIsSheetOpen(false);
      setEditingTask(null);
      fetchData();
    } catch (_err) { toast.error('Une erreur est survenue'); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async (taskId) => {
    try {
      await axios.delete(`${API}/tasks/${taskId}`, { withCredentials: true });
      setDeleteConfirm(null);
      toast.success('Supprimé avec succes');
      fetchData();
    } catch (_err) { toast.error('Erreur lors de la suppression'); }
  };

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const params = filterChantier !== 'all' ? `?chantier_id=${filterChantier}` : '';
      const response = await safeBlobGet(`${API}/tasks/export/pdf${params}`);
      downloadBlob(response, 'Planning_OPC.pdf');
      toast.success('PDF exporte avec succes');
    } catch (_err) { toast.error(_err.message || "Erreur lors de l'export PDF"); }
    finally { setExporting(false); }
  };

  const getChantierName = (chantierId) => {
    const c = (chantiers || []).find(ch => ch && ch.id === chantierId);
    return c?.name || 'N/A';
  };

  const filteredTasks = (tasks || []).filter(t => {
    if (!t) return false;
    return filterChantier === 'all' || t.chantier_id === filterChantier;
  });

  const stats = {
    total: filteredTasks.length,
    pending: filteredTasks.filter(t => t.status === 'pending').length,
    inProgress: filteredTasks.filter(t => t.status === 'in_progress').length,
    completed: filteredTasks.filter(t => t.status === 'completed').length,
    delayed: filteredTasks.filter(t => t.status === 'delayed').length,
  };

  const globalProgress = stats.total > 0
    ? Math.round(filteredTasks.reduce((sum, t) => sum + (t.progress || (t.status === 'completed' ? 100 : 0)), 0) / stats.total)
    : 0;

  // Gantt data
  const ganttData = useMemo(() => {
    if (filteredTasks.length === 0) return { lots: [], milestones: [], timeStart: new Date(), timeEnd: new Date(), totalDays: 30 };
    const validTasks = filteredTasks.filter(t => t.start_date && t.end_date);
    if (validTasks.length === 0) return { lots: [], milestones: [], timeStart: new Date(), timeEnd: new Date(), totalDays: 30 };

    const regularTasks = validTasks.filter(t => (t.task_type || 'task') === 'task');
    const milestones = validTasks.filter(t => t.task_type === 'milestone');

    let minDate = parseISO(validTasks[0].start_date);
    let maxDate = parseISO(validTasks[0].end_date);
    validTasks.forEach(t => {
      const s = parseISO(t.start_date);
      const e = parseISO(t.end_date);
      if (s < minDate) minDate = s;
      if (e > maxDate) maxDate = e;
    });

    const timeStart = addDays(minDate, -2);
    const timeEnd = addDays(maxDate, 3);
    const totalDays = Math.max(differenceInDays(timeEnd, timeStart), 7);

    const lotMap = {};
    regularTasks.forEach(t => {
      const lotName = t.lot || 'Lot non defini';
      if (!lotMap[lotName]) lotMap[lotName] = [];
      lotMap[lotName].push(t);
    });

    const lots = Object.entries(lotMap).map(([name, lotTasks]) => ({
      name,
      tasks: lotTasks.sort((a, b) => parseISO(a.start_date) - parseISO(b.start_date)),
    }));

    return { lots, milestones, timeStart, timeEnd, totalDays };
  }, [filteredTasks]);

  const timeLabels = useMemo(() => {
    if (ganttData.totalDays <= 0) return [];
    const labels = [];
    let current = ganttData.timeStart;
    while (current <= ganttData.timeEnd) {
      labels.push(current);
      current = addDays(current, ganttData.totalDays > 60 ? 7 : ganttData.totalDays > 30 ? 3 : 1);
    }
    return labels;
  }, [ganttData]);

  if (loading) {
    return (
      <AppShell>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-10 h-10 animate-spin text-[#FF6B00]" />
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="space-y-5" data-testid="planning-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="h-10 w-10 md:hidden"
              onClick={() => navigate('/dashboard')} data-testid="back-btn">
              <ArrowLeft size={22} />
            </Button>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-[#111827]">Planning OPC</h1>
              <p className="text-sm text-[#4B5563]">{stats.total} tâche(s) planifiee(s)</p>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            {[
              { id: 'gantt', icon: ChartBar, label: 'Gantt' },
              { id: 'calendar', icon: CalendarBlank, label: 'Calendrier' },
              { id: 'list', icon: ListChecks, label: 'Liste' },
            ].map(v => (
              <Button key={v.id} variant={viewMode === v.id ? 'default' : 'outline'}
                onClick={() => setViewMode(v.id)}
                className={`h-10 text-xs ${viewMode === v.id ? 'bg-[#FF6B00] hover:bg-orange-700' : ''}`}
                data-testid={`view-${v.id}`}>
                <v.icon size={16} className="mr-1.5" /> {v.label}
              </Button>
            ))}
            <Button onClick={() => openCreateSheet()} className="h-10 bg-[#111827] hover:bg-gray-800 text-xs" data-testid="new-task-btn">
              <Plus size={16} className="mr-1.5" /> Tâche
            </Button>
            <Button onClick={() => openCreateSheet(null, true)} variant="outline" className="h-10 text-xs border-[#FF6B00] text-[#FF6B00] hover:bg-[#FF6B00]/10" data-testid="new-milestone-btn">
              <Flag size={16} className="mr-1.5" weight="fill" /> Jalon
            </Button>
            <Button onClick={handleExportPDF} disabled={exporting || stats.total === 0}
              variant="outline" className="h-10 text-xs" data-testid="export-pdf-btn">
              {exporting ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <FilePdf size={16} className="mr-1.5" />}
              Export PDF
            </Button>
            <Button onClick={() => setEmailOpen(true)} disabled={stats.total === 0}
              variant="outline" className="h-10 text-xs text-emerald-700 border-emerald-300" data-testid="email-planning-btn">
              <span className="mr-1.5">✉️</span> Envoyer
            </Button>
            <Button onClick={() => setImportOpen(true)} variant="outline"
              className="h-10 text-xs border-[#0055FF] text-[#0055FF] hover:bg-[#0055FF]/10"
              data-testid="import-excel-btn">
              <Upload size={16} className="mr-1.5" /> Importer Excel
            </Button>
          </div>
        </div>

        <GlobalProgressHero stats={stats} globalProgress={globalProgress} />

        {/* Filter */}
        <Select value={filterChantier} onValueChange={setFilterChantier}>
          <SelectTrigger className="w-full md:w-72 h-11" data-testid="filter-chantier-planning">
            <SelectValue placeholder="Filtrer par chantier" />
          </SelectTrigger>
          <SelectContent position="popper" className="z-[100]">
            <SelectItem value="all" className="py-3">Tous les chantiers</SelectItem>
            {(chantiers || []).filter(c => c && c.id && c.name).map(c => (
              <SelectItem key={c.id} value={String(c.id)} className="py-3">{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {viewMode === 'gantt' && (
          <div data-testid="gantt-view">
            <GanttChart
              filteredTasks={filteredTasks}
              ganttData={ganttData}
              timeLabels={timeLabels}
              onEditTask={openEditSheet}
              onCreateSheet={openCreateSheet}
            />
          </div>
        )}

        {viewMode === 'calendar' && (
          <CalendarView
            filteredTasks={filteredTasks}
            chantiers={chantiers}
            currentMonth={currentMonth}
            setCurrentMonth={setCurrentMonth}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            getChantierName={getChantierName}
            onCreateSheet={openCreateSheet}
            onEditTask={openEditSheet}
          />
        )}

        {viewMode === 'list' && (
          <TaskListView
            filteredTasks={filteredTasks}
            getChantierName={getChantierName}
            onEditTask={openEditSheet}
            onDeleteConfirm={setDeleteConfirm}
            onCreateSheet={openCreateSheet}
          />
        )}
      </div>

      <TaskFormSheet
        open={isSheetOpen} onOpenChange={setIsSheetOpen}
        chantiers={chantiers} entreprises={entreprises}
        editingTask={editingTask} formData={formData} setFormData={setFormData}
        submitting={submitting}
        onSubmit={handleSubmit}
        onFetchEntreprises={fetchEntreprises}
      />

      <DeleteConfirmDialog
        item={deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={handleDelete}
        titlePrefix={`Supprimér ${deleteConfirm?.task_type === 'milestone' ? 'le jalon' : 'la tâche'}`}
        entityLabel={deleteConfirm?.title}
      />

      <ImportPlanningDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        chantiers={chantiers}
        defaultChantierId={filterChantier !== 'all' ? filterChantier : null}
        onImportSuccess={() => { setImportOpen(false); fetchData(); }}
      />

      <QuickAddFAB chantiers={chantiers} />
      <SendDocumentEmailDialog
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        docType="planning"
        pdfUrl={`${API}/tasks/export/pdf${filterChantier !== 'all' ? `?chantier_id=${filterChantier}` : ''}`}
        filename={`Planning_CHANTIX_${new Date().toISOString().slice(0,10)}.pdf`}
        subject="[CHANTIX] Planning chantier"
        docTitle="Planning des tâches du chantier"
        suggestedRecipients={[]}
      />

    </AppShell>
  );
}
